import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AlertModule, ButtonModule, SpinnerModule } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { TranslatePipe } from 'i18n';
import { Logger } from 'shared';
import {
  NewsletterSubscriptionService,
  type NewsletterSubscriptionState
} from '../../services/newsletter-subscription.service';

/**
 * Dashboard offer to re-subscribe to the newsletter, for users who opted out.
 *
 * Renders nothing at all in every other case — subscribed, hard-bounce blocked,
 * impersonating, or while the state is still loading. The backend decides that in
 * one field (`can_subscribe`) so the rule cannot drift between the brands that
 * drop this in.
 *
 * Drop `<app-newsletter-optin></app-newsletter-optin>` anywhere a logged-in user
 * is present.
 */
@Component({
  selector: 'app-newsletter-optin',
  standalone: true,
  imports: [CommonModule, AlertModule, ButtonModule, IconModule, SpinnerModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (subscribed()) {
      <!-- Confirmation of what just happened. Shown only after the click: an
           already-subscribed user never sees this component at all. -->
      <c-alert color="success" class="d-flex align-items-start mb-0">
        <svg cIcon name="cilCheckCircle" size="xl" class="me-3 flex-shrink-0"></svg>
        <div>
          <h5 class="alert-heading mb-1">{{ 'newsletter.optin.done_title' | translate }}</h5>
          <p class="mb-0">
            {{ 'newsletter.optin.done_text' | translate: { email: state()?.email ?? '' } }}
          </p>
        </div>
      </c-alert>
    } @else if (state()?.can_subscribe) {
      <c-alert color="info" class="mb-0">
        <div class="d-flex align-items-start">
          <svg cIcon name="cilEnvelopeClosed" size="xl" class="me-3 flex-shrink-0"></svg>
          <div class="flex-grow-1">
            <h5 class="alert-heading mb-1">{{ 'newsletter.optin.title' | translate }}</h5>
            <p class="mb-2">
              {{ 'newsletter.optin.text' | translate: { email: state()?.email ?? '' } }}
            </p>
            @if (failed()) {
              <p class="text-danger mb-2">{{ 'newsletter.optin.error' | translate }}</p>
            }
            <button cButton color="primary" [disabled]="saving()" (click)="subscribe()">
              @if (saving()) {
                <c-spinner size="sm" class="me-2" aria-hidden="true"></c-spinner>
              }
              {{ 'newsletter.optin.button' | translate }}
            </button>
          </div>
        </div>
      </c-alert>
    }
  `
})
export class NewsletterOptinComponent implements OnInit {
  private newsletter = inject(NewsletterSubscriptionService);
  private logger = new Logger('NewsletterOptinComponent');

  state = signal<NewsletterSubscriptionState | null>(null);
  /** Set once the user has opted back in from here, to show the confirmation. */
  subscribed = signal(false);
  saving = signal(false);
  failed = signal(false);

  ngOnInit(): void {
    this.newsletter.status().subscribe({
      next: state => this.state.set(state),
      // A backend without the endpoint (or a hiccup) must not put an error on the
      // dashboard: the component simply stays invisible.
      error: err => this.logger.debug('Newsletter subscription state unavailable', err)
    });
  }

  subscribe(): void {
    if (this.saving()) return;
    this.saving.set(true);
    this.failed.set(false);

    this.newsletter.subscribe().subscribe({
      next: state => {
        this.state.set(state);
        this.subscribed.set(state.subscribed);
        this.failed.set(!state.subscribed);
        this.saving.set(false);
      },
      error: err => {
        this.logger.error('Newsletter re-subscribe failed', err);
        this.failed.set(true);
        this.saving.set(false);
      }
    });
  }
}
